import deepmerge from 'deepmerge';

import { getGlobalWindow } from '../helpers/window';
import { defaultMvtConfig, defaultProductPageMvtConfig } from './defaults';
import { MvtConfig, ToastNotificationConfig } from './types';

export function mergeToastNotification(
	toastNotificationA: undefined | ToastNotificationConfig,
	toastNotificationB: undefined | ToastNotificationConfig
): undefined | ToastNotificationConfig {
	if (!toastNotificationA && !toastNotificationB) {
		return undefined;
	}

	if (toastNotificationB) {
		const namespaceFallback =
			(toastNotificationA && toastNotificationA.namespace) || 'coax';
		const contentFallback =
			(toastNotificationA && toastNotificationA.content) || undefined;
		const waitTimeFallback =
			(toastNotificationA && toastNotificationA.waitTime) || undefined;
		const dismissTimeToLiveFallback =
			(toastNotificationA && toastNotificationA.dismissTimeToLive) || undefined;

		let content =
			toastNotificationB && toastNotificationB.content
				? toastNotificationB.content
				: false;
		content = !content && contentFallback ? contentFallback : content;

		if (!content) {
			return undefined;
		}

		const namespace = namespaceFallback ? namespaceFallback : 'coax';

		return {
			namespace: toastNotificationB.namespace
				? toastNotificationB.namespace
				: namespace,
			content: content,
			waitTime: toastNotificationB.hasOwnProperty('waitTime')
				? toastNotificationB.waitTime
				: waitTimeFallback,
			dismissTimeToLive: toastNotificationB.hasOwnProperty('dismissTimeToLive')
				? toastNotificationB.dismissTimeToLive
				: dismissTimeToLiveFallback,
		};
	}

	return toastNotificationA;
}

function customConfigMerger(
	key: string
): ((x: any, y: any) => any) | undefined {
	if (key === 'toastNotification') {
		return mergeToastNotification;
	}

	return undefined;
}

// Export as function expression so we can spy/mock the function from the same file
// https://github.com/facebook/jest/issues/936#issuecomment-545080082
export const getProductPageConfig = (
	baseConfig: MvtConfig = {},
	configOverride: MvtConfig = {}
): MvtConfig => {
	const globalWindow = getGlobalWindow();
	const configOverrideCopy = { ...configOverride };
	const isInStock =
		globalWindow.digitalData &&
		globalWindow.digitalData.page &&
		globalWindow.digitalData.page.product &&
		globalWindow.digitalData.page.product.isInStock
			? true
			: false;

	if (!isInStock) {
		configOverrideCopy.toastNotification = {
			namespace: 'coax',
			content: `<div class="md:hidden" style="max-width: 188px"><h6 class="mb-2 text-gray-100 text-title">
		We've got more!
		</h6><p class="text-body-sm md:mb-3 mb-1">
		Chat to us to see if we can find you<br class="md:block hidden" /> something similar in stock.
		</p><hr class="md:block hidden m-0 -mx-4 h-0 border-0 border-b border-solid border-gray-40" /><div class="-mx-4 -mb-4"> <button data-tag-name="open chatbot" data-tag-ao-assistant="FAST_TRACK_WELCOME" data-tag-ao-assistant-system="true" data-tag-ao-assistant-interaction="true" type="button" class="text-link text-body-sm md:hidden px-2 py-1 mb-3 ml-2 font-bold bg-white cursor-pointer" > Let\'s chat </button></div></div><div class="md:block hidden"><h6 class="mb-2 text-gray-100 text-title">
		We've got more!
		</h6><p class="text-body-sm md:mb-3 mb-1" style="width: 234px">
		Chat to us to see if we can find you<br class="md:block hidden" /> something similar in stock.
		</p><hr class="md:block hidden m-0 -mx-4 h-0 border-0 border-b border-solid border-gray-40" /><div class="-mx-4 -mb-4"> <button data-tag-name="open chatbot" data-tag-ao-assistant="FAST_TRACK_WELCOME" data-tag-ao-assistant-system="true" data-tag-ao-assistant-interaction="true" type="button" class="md:block hover:bg-cta-actionhover hover:text-white text-cta-action text-title-sm flex items-center justify-center hidden w-full px-3 m-0 bg-white rounded-md rounded-t-none cursor-pointer" style="padding-top: 15px; padding-bottom: 15px;" > Let\'s chat </button></div></div>`,
		};
	}

	return deepmerge(baseConfig, configOverrideCopy, {
		customMerge: customConfigMerger,
	});
};

// Make sure to inform Analytics if any new keys are added to mvtConfig
export function getMvtConfig(): MvtConfig {
	const globalWindow = getGlobalWindow();

	if (
		globalWindow.digitalData &&
		globalWindow.digitalData.page &&
		globalWindow.digitalData.page.pageInfo &&
		globalWindow.digitalData.page.pageInfo.pageType === 'Product Page'
	) {
		return getProductPageConfig(
			defaultProductPageMvtConfig,
			globalWindow.chatbotMvtConfig ? globalWindow.chatbotMvtConfig : {}
		);
	}

	return globalWindow.chatbotMvtConfig
		? deepmerge(defaultMvtConfig, globalWindow.chatbotMvtConfig, {
				customMerge: customConfigMerger,
		  })
		: defaultMvtConfig;
}
